/**
 * Service Appointment status — single source of truth.
 *
 * The PG export schema stores English canonical values
 *   None | Scheduled | Dispatched | In Progress | Completed | Cannot Complete | Canceled
 * but legacy code paths and a few backend writes have leaked Hebrew
 * variants ('מתוזמנת', 'תואמה התקנה', …) into both the codebase and
 * occasionally the data. Every comparison + display goes through one
 * of the helpers below so we agree on a single value, in English, and
 * the label-language stays a presentation concern.
 *
 * Rules:
 *   - Use SA_STATUS.* constants in code, never bare strings.
 *   - Backend writes go through normalizeStatus() so Hebrew never
 *     reaches the DB column.
 *   - Display always uses getStatusHebrew(); it accepts any variant.
 */

export const SA_STATUS = {
  NONE: 'None',
  SCHEDULED: 'Scheduled',
  DISPATCHED: 'Dispatched',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANNOT_COMPLETE: 'Cannot Complete',
  CANCELED: 'Canceled',
};

// Canonical English -> display Hebrew
export const SA_STATUS_HEBREW = {
  'None': 'ללא סטטוס',
  'Scheduled': 'פגישה מתוזמנת',
  'Dispatched': 'תואמה התקנה',
  'In Progress': 'בתהליך',
  'Completed': 'הושלם',
  'Cannot Complete': 'לא ניתן להשלים',
  'Canceled': 'פגישה בוטלה',
};

// Hebrew variants -> English. The map deliberately includes shorthand
// forms ('מתוזמנת' alone) and the canonical multi-word form so old
// records and new writes both pass through the same funnel.
export const SA_STATUS_HEBREW_TO_ENGLISH = {
  'ללא סטטוס': 'None',
  'ללא': 'None',
  'פגישה מתוזמנת': 'Scheduled',
  'מתוזמנת': 'Scheduled',
  'תואמה התקנה': 'Dispatched',
  'תואמה': 'Dispatched',
  'בתהליך': 'In Progress',
  'הושלם': 'Completed',
  'לא ניתן להשלים': 'Cannot Complete',
  'פגישה בוטלה': 'Canceled',
  'בוטלה': 'Canceled',
};

export const SA_STATUS_COLORS = {
  'None': '#9E9E9E',
  'Scheduled': '#FF9800',
  'Dispatched': '#2196F3',
  'In Progress': '#9C27B0',
  'Completed': '#4CAF50',
  'Cannot Complete': '#F44336',
  'Canceled': '#9E9E9E',
};

/** Coerce any incoming status (English / Hebrew / shorthand / null)
 *  to its canonical English form. Unknown values pass through so we
 *  don't silently rewrite something we didn't recognise.
 */
export function normalizeStatus(raw) {
  if (raw === null || raw === undefined || raw === '') return SA_STATUS.NONE;
  const s = String(raw).trim();
  if (s in SA_STATUS_HEBREW) return s;            // already English
  if (s in SA_STATUS_HEBREW_TO_ENGLISH) return SA_STATUS_HEBREW_TO_ENGLISH[s];
  return s;
}

export function getStatusHebrew(raw) {
  if (!raw) return '';
  const eng = normalizeStatus(raw);
  return SA_STATUS_HEBREW[eng] || raw;
}

export function getStatusColor(raw) {
  const eng = normalizeStatus(raw);
  return SA_STATUS_COLORS[eng] || '#607D8B';
}

/** Picklist options for forms — value=English (DB), label=Hebrew (UI). */
export const SA_STATUS_OPTIONS = Object.entries(SA_STATUS_HEBREW).map(
  ([value, label]) => ({ value, label })
);
